import { useEffect, useRef, useState } from "react";
import { CONSENT } from "@/constants/testIds";

/**
 * Lightweight signature pad — black 2px stroke on white, 280×80.
 * Exposes value via `onChange(dataUrl)` and a `Clear` text link.
 */
export function SignaturePad({ onChange, width = 280, height = 80 }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#1C1917";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) {
      setHasInk(true);
      onChange?.(c.toDataURL("image/png"));
    }
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (hasInk) onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        data-testid={CONSENT.signaturePad}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="rounded-xl border border-white/80 bg-white shadow-inner cursor-crosshair touch-none"
        style={{ width, height }}
      />
      <button
        type="button"
        onClick={clear}
        data-testid={CONSENT.clearSignatureBtn}
        className="mt-1 font-inter text-[11.5px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] underline"
      >
        Clear
      </button>
    </div>
  );
}

export default SignaturePad;
